import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { TwinPortraitStack } from './TwinPortraitStack'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { cn } from '@/lib/utils'
import { populationsApi } from '@/lib/api'
import { FeaturedTwinListItem, AudienceSummary } from '@/lib/api/client'
import { OnboardingWizardState, TargetingType } from '@/types/components/onboarding'
import { FadeUp } from '../../FadeUp'
import { getTopAudiences } from './scoreAudiences'

interface ChatSectionProps {
  /** Controls which set of synthetic profiles to display (B2B or B2C). */
  targetingType?: TargetingType
  /**
   * Wizard state used to derive the top matched audiences and fetch their
   * real featured twins. Falls back to static sample twins when absent.
   */
  wizardState?: OnboardingWizardState
  className?: string
}

/**
 * Fetches featured twins from the top matched audiences.
 * Returns up to 3 twins across the ranked audience list.
 * Cache scope: per Getting Started session, keyed on top audience IDs.
 */
function usePersonalizedTwins(
  topAudienceIds: Array<string>,
): { twins: Array<FeaturedTwinListItem>; isLoading: boolean } {
  const cacheKey = topAudienceIds.length > 0
    ? `personalized-twins-${topAudienceIds.slice(0, 2).join('-')}`
    : null

  const { data, isLoading } = useSWR<Array<FeaturedTwinListItem>>(
    cacheKey,
    async () => {
      const results = await Promise.all(
        topAudienceIds.slice(0, 2).map((id) =>
          populationsApi.getFeaturedTwins(id).catch(() => [] as Array<FeaturedTwinListItem>),
        ),
      )
      // Flatten and deduplicate by personaId, cap at 3
      const seen = new Set<string>()
      return results.flat().filter((t) => {
        if (seen.has(t.personaId)) return false
        seen.add(t.personaId)
        return true
      }).slice(0, 3)
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  )

  return { twins: data ?? [], isLoading }
}

export function ChatSection({ targetingType = 'B2B', wizardState, className }: ChatSectionProps) {
  const { t } = useTranslation('onboarding')
  // Read the already-fetched audience list from SWR cache (warmed by AudiencesSection in section 1)
  const { data: allAudiences } = useSWR<Array<AudienceSummary>>('home-audiences')

  const resolvedAudienceIds = useMemo(() => {
    if (!wizardState || !allAudiences) return []
    const hasSelections =
      wizardState.selectedIndustries.length > 0 || wizardState.selectedCountries.length > 0
    if (!hasSelections) return []
    const top = getTopAudiences(allAudiences, wizardState, 2)
    return top.map((a) => a.id)
  }, [wizardState, allAudiences])

  const { twins, isLoading } = usePersonalizedTwins(resolvedAudienceIds)

  // Show personalized twins only when we have enough to fill the visual stack (3).
  // A single card looks broken; fall through to the polished static stack instead.
  const MIN_TWINS_FOR_PERSONALIZED = 3
  const hasPersonalizedTwins = !isLoading && twins.length >= MIN_TWINS_FOR_PERSONALIZED

  return (
    <div className={cn('flex flex-col items-center gap-6', className)}>
      {/* Heading + subtext — delay 0 */}
      <FadeUp delay={0} className="flex flex-col gap-2 text-center">
        <Heading level={2} size="3xl">
          {t('guide.chatTitle')}
        </Heading>
        <Text color="muted" className="max-w-sm">
          {t('guide.chatBody')}
        </Text>
      </FadeUp>

      {/* Portrait stack — delay 120ms */}
      <FadeUp delay={120}>
        {hasPersonalizedTwins ? (
          <PersonalizedTwinStack twins={twins} className="py-4" />
        ) : (
          <TwinPortraitStack targetingType={targetingType} className="py-4" />
        )}
      </FadeUp>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PersonalizedTwinStack — renders real featured twins from matched audiences.
// Uses the same card layout as TwinPortraitStack but with live API data.
// ---------------------------------------------------------------------------

// --onboarding-portrait-deep corrects the dark-mode --primary-dark drift.
// See TwinPortraitStack.tsx for the full explanation.
const GRADIENTS = [
  'from-primary via-primary to-[hsl(var(--onboarding-portrait-deep))]',
  'from-chart-secondary via-primary to-chart-accent',
  'from-chart-warning via-[hsl(var(--onboarding-portrait-deep))] to-primary',
]

interface PersonalizedTwinStackProps {
  twins: Array<FeaturedTwinListItem>
  className?: string
}

/** One card's photo — its own component so the thumbnail/original fallback
 *  state (finding 7, image compression) is scoped per twin instead of being
 *  called inside the surrounding `.map`. */
function PersonalizedTwinPhoto({ imageUrl, alt }: { imageUrl: string; alt: string }) {
  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 256)

  return (
    <img
      src={displayUrl}
      alt={alt}
      className="aspect-[3/4] w-full object-cover object-top"
      onError={() => handleImageError()}
    />
  )
}

function PersonalizedTwinStack({ twins, className }: PersonalizedTwinStackProps) {
  return (
    <div className={cn('flex items-center justify-center -space-x-8 md:-space-x-12', className)}>
      {twins.map((twin, idx) => {
        const isCenter = idx === 1
        const gradient = GRADIENTS[idx % GRADIENTS.length]
        const imageUrl = twin.imageUrl ?? ''
        const name = twin.personaName ?? `Twin ${idx + 1}`
        const role = twin.personaTagline ?? ''
        const segment = twin.demographics?.segment ?? twin.demographics?.industry ?? ''

        return (
          <div
            key={twin.personaId}
            style={{ zIndex: isCenter ? 20 : 10 - Math.abs(idx - 1) }}
            className={cn(
              'relative transition-transform duration-300 hover:-translate-y-2',
              isCenter ? 'w-44 md:w-56' : 'w-36 md:w-44',
            )}
          >
            <div
              className={cn(
                'relative overflow-hidden rounded-2xl bg-gradient-to-br shadow-elevated',
                gradient,
              )}
            >
              {imageUrl && <PersonalizedTwinPhoto imageUrl={imageUrl} alt={name} />}
              <div className="absolute inset-0 bg-gradient-to-t from-media/80 via-media/20 to-transparent" />
              {segment && (
                <div className="absolute right-3 top-3">
                  <Text
                    as="span"
                    size="xs"
                    className="rounded-full bg-black/40 px-3 py-1 text-white shadow-sm backdrop-blur-md"
                  >
                    {segment}
                  </Text>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-0.5 p-4">
                <Text as="span" size="sm" weight="medium" className="text-media-foreground">
                  {name}
                </Text>
                {role && (
                  <Text size="xs" className="text-media-foreground/80">
                    {role}
                  </Text>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
